/**
 * A ranking one person makes alone, kept as a session result (roadmap S-16).
 *
 * What's Next (Morning Boot's top three) and First 100 Days (Day 101) both
 * rank the person's own items. The order is never a list kept in the browser:
 * it is the Session app's Stack Ranking, run with one participant, through the
 * same steps as every session — create (createSessionWithFeedback), open
 * (openForParticipants), the person's answer (submitParticipantResponse) and
 * close (closeAndComputeResults, which saves through run_session and the one
 * counting rule). So the ranking is a real, kept result, on the session list
 * and the item's history like any other.
 *
 * Which app made a ranking rides in the session's toolConfig, as the
 * blueprint apps do: presentation, not a governed fact (Doc 31 first law).
 */

import { getCurrentWorkspaceId, getSafeSupabaseClient } from '@/lib/supabaseSync';
import { createSessionWithFeedback } from '@/lib/sessions/createSessionFlow';
import { useToolSessionStore } from '@/stores/prioritization/toolSessionStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import type { OriginModule } from '@/types/prioritization/originModule';
import {
  closeAndComputeResults,
  findActiveToolSessionId,
  openForParticipants,
  sessionToolItems,
} from './session/sessionAppActions';

/** The toolConfig key naming the app that made a personal ranking. */
export const PERSONAL_RANKING_APP_KEY = 'personal_app_id';

export interface RankAloneInput {
  /** The app making the ranking (registry appId), e.g. 'whats-next'. */
  appId: string;
  /** The session's name, as the person will see it on their session list. */
  name: string;
  /** Item ids in the person's order, most important first. */
  order: string[];
  userId: string;
  userName: string;
  originModule?: OriginModule;
}

export type RankAloneResult = { ok: true; sessionId: string } | { ok: false; error: string };

/** Run a Stack Ranking with one participant and keep its result. */
export async function rankAlone(input: RankAloneInput): Promise<RankAloneResult> {
  const order = [...new Set(input.order)];
  if (order.length < 2) return { ok: false, error: 'Rank at least two items.' };
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: 'No workspace is open.' };

  const created = await createSessionWithFeedback({
    name: input.name,
    facilitatorId: input.userId,
    createdBy: input.userId,
    intent: 'rank_priorities',
    sessionContextMode: 'app_linked',
    itemIds: order,
    selectedToolId: 'stack_ranking',
    toolConfig: { [PERSONAL_RANKING_APP_KEY]: input.appId },
    modeConfig: { timingMode: 'async' },
    workspaceId,
    ...(input.originModule ? { originModule: input.originModule } : {}),
  });
  if (!created.ok || !created.session || !created.sessionId) {
    return { ok: false, error: created.error ?? 'The ranking could not be started.' };
  }
  const sessionId = created.sessionId;

  const opened = await openForParticipants(created.session, input.userId, input.userName);
  if (!opened.success) return { ok: false, error: opened.error ?? 'The ranking could not be opened.' };

  const toolSessionId = await findActiveToolSessionId(sessionId);
  if (!toolSessionId) return { ok: false, error: 'The ranking could not be opened.' };

  // The person's answer: the same shape the Stack Ranking tool submits (rank 1
  // first, a higher score for a higher place).
  const names = new Map(sessionToolItems(created.session).map((item) => [item.id, item.name]));
  const results = order.map((id, index) => ({
    id,
    name: names.get(id) ?? id,
    rank: index + 1,
    score: order.length - index,
  }));
  try {
    await useToolSessionStore.getState().submitParticipantResponse({
      toolSessionId,
      workspaceId,
      participantId: input.userId,
      participantName: input.userName,
      results,
    });
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Your order was not saved.' };
  }

  const session = useSessionStore.getState().getSession(sessionId) ?? created.session;
  const closed = await closeAndComputeResults(session);
  if (!closed.success) return { ok: false, error: closed.error ?? 'The ranking could not be saved.' };
  return { ok: true, sessionId };
}

export interface KeptRanking {
  sessionId: string;
  /** The kept order, most important first. */
  rankedItemIds: string[];
  createdAt: string;
}

/** The order a kept ranking holds: its ranking output, first to last. */
export function rankedIdsOf(rankingOutput: unknown): string[] {
  const out = (rankingOutput ?? {}) as {
    rankedItemIds?: unknown;
    orderedItems?: Array<{ id?: unknown }>;
  };
  if (Array.isArray(out.rankedItemIds)) return out.rankedItemIds.filter((id): id is string => typeof id === 'string');
  if (Array.isArray(out.orderedItems)) {
    return out.orderedItems.map((item) => item.id).filter((id): id is string => typeof id === 'string');
  }
  return [];
}

/**
 * The person's latest kept ranking from this app, made on or after `since`
 * (Morning Boot: today; Day 101: any time). Read from the database, so it
 * survives a reload.
 */
export async function findKeptRanking(
  appId: string,
  userId: string,
  since?: Date,
): Promise<KeptRanking | null> {
  const supabase = getSafeSupabaseClient();
  const workspaceId = getCurrentWorkspaceId();
  if (!supabase || !workspaceId) return null;
  let query = supabase
    .from('prioritization_sessions')
    .select('id, ranking_output, created_at')
    .eq('workspace_id', workspaceId)
    .eq('created_by', userId)
    .eq('state', 'RUN_COMPLETE')
    .eq(`tool_config->>${PERSONAL_RANKING_APP_KEY}`, appId)
    .order('created_at', { ascending: false })
    .limit(1);
  if (since) query = query.gte('created_at', since.toISOString());
  const { data } = await query.maybeSingle();
  if (!data) return null;
  const row = data as { id: string; ranking_output: unknown; created_at: string };
  const rankedItemIds = rankedIdsOf(row.ranking_output);
  if (rankedItemIds.length === 0) return null;
  return { sessionId: row.id, rankedItemIds, createdAt: row.created_at };
}
